import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import InvoicesList, { transformInvoicesArray } from './InvoicesList';
import InvoiceDetailsPopup from './InvoiceDetailsPopup';
import LoaderSpinner from '../../common/Loader';
import { generateInvoicePDF } from '../../../utils/pdfGenerator';

// Import hooks from Hooks folder
import {
    useFilterData,
    useDateRangeFilters,
    useCompanySellerFilters,
    useInvoiceData,
} from './Hooks/Invoices.hooks';

// Import subcomponents from Subcomponents folder
import {
    InvoiceHeader,
    InvoiceDateFiltersWithToggle,
    InvoiceAdvancedFilter,
} from './Subcomponents/Invoices.subcomponents';

// Import utilities
import { getDateRange } from './utils/Invoices.dateRange.utils';

const Invoices = () => {
    const dispatch = useDispatch();
    const { showAccountingMenu } = useSelector(state => state.accountingReducer);
    
    // Get user role info from Redux
    const { userRole, roleList } = useSelector(state => ({
        userRole: state.userReducer?.userData?.userRole || state.userReducer?.userRole,
        roleList: state.userReducer?.roleList || [],
    }));

    // Use custom hooks
    const { filterData, setfilterData, defaultFilterData } = useFilterData();
    const { selectedDateRange, setSelectedDateRange, handleDateRangeFilter, handleDateFieldChange } = useDateRangeFilters(filterData, setfilterData);
    const { companyList, sellerList, handleMultiSelect: hookHandleMultiSelect, performAutoSelectSeller } = useCompanySellerFilters(dispatch);
    const { invoiceList, invoiceListStatus, totalRecords, getAllInvoices } = useInvoiceData(filterData);

    // Fetch invoices ONLY when pagination or sorting changes
    useEffect(() => {
        if (filterData.dateFrom && filterData.dateTo) {
            getAllInvoices();
        }
    }, [filterData.pageSize, filterData.pageIndex, filterData.sortColumn, filterData.sortOrder]);

    // Auto-select seller when sellers are refetched after company selection
    useEffect(() => {
        performAutoSelectSeller(filterData, setfilterData);
    }, [sellerList]);

    const handleFilterToggle = (key, value) => {
        setfilterData({ ...filterData, [key]: value });
    };

    const handleReset = (e, type) => {
        if (type === 'clear') {
            let data = JSON.parse(JSON.stringify(defaultFilterData));
            data.showFilter = false;
            data.pageSize = filterData.pageSize;
            const [startDate, endDate] = getDateRange('This Month');
            data.dateFrom = startDate;
            data.dateTo = endDate;
            setfilterData(data);
            setSelectedDateRange('This Month');
            return;
        }
        setfilterData({ ...filterData, searchText: '' });
    };

    const handleFilter = () => {
        if (filterData.pageIndex === 1) {
            getAllInvoices();
        } else {
            setfilterData({ ...filterData, pageIndex: 1 });
        }
    };

    const handleMultiSelect = (value, fieldName) => {
        // Reset auto-select tracker when user manually changes company/seller
        hookHandleMultiSelect(value, fieldName, filterData, setfilterData);
    };

    const [showInvoiceDetailsPopup, setShowInvoiceDetailsPopup] = useState(false);

    // Transform API data to required format
    const transformedInvoiceData = transformInvoicesArray(invoiceList);

    // Sample invoice for testing
    const testInvoice = {
        invoiceNumber: 'INV-2026-001',
        invoiceDate: '2026-05-01',
        dueDate: '2026-05-31',
        status: 'Pending',
        companyName: 'Test Company Inc.',
        sellerName: 'Test Seller',
        sellerCode: 'TS-001',
        amount: 500,
    };

    const handleDownloadPDF = invoice => {
        const invoiceElement = document.getElementById('invoice-pdf-content');
        if (!invoiceElement) {
            console.error('Invoice PDF content element not found');
            alert('Unable to generate PDF. Invoice content not found.');
            return;
        }
        try {
            const filename = invoice.invoiceNumber ? `${invoice.invoiceNumber}.pdf` : 'invoice.pdf';
            generateInvoicePDF(invoiceElement, filename);
            console.log(`PDF generated: ${filename}`);
        } catch (error) {
            console.error('Error generating PDF:', error);
            alert('Failed to download invoice PDF. Please try again.');
        }
    };

    const handleResendEmail = invoice => {
        console.log('Resend email for:', invoice.invoiceNumber);
    };

    const handleWhatsApp = invoice => {
        console.log('Send WhatsApp for:', invoice.invoiceNumber);
    };

    return (
        <div className={`${showAccountingMenu ? 'col-10' : 'col-12'}`}>
            <div className="">
                <InvoiceHeader />                
                <InvoiceDetailsPopup
                    show={showInvoiceDetailsPopup}
                    showPopup={showInvoiceDetailsPopup}
                    hidePopup={setShowInvoiceDetailsPopup}
                    invoiceRecord={testInvoice}
                    onDownloadPDF={handleDownloadPDF}
                    onResendEmail={handleResendEmail}
                    onWhatsApp={handleWhatsApp}
                />
                <InvoiceDateFiltersWithToggle
                    selectedDateRange={selectedDateRange}
                    onDateRangeChange={handleDateRangeFilter}
                    onFilterToggle={handleFilterToggle}
                    isFilterOpen={filterData.showFilter}
                />
                {invoiceListStatus === 'FAILED' && (
                    <div
                        data-testid="invoice-error-message"
                        style={{
                            backgroundColor: '#f8d7da',
                            color: '#721c24',
                            padding: '12px',
                            borderRadius: '4px',
                            marginBottom: '15px',
                            border: '1px solid #f5c6cb',
                        }}
                    >
                        <strong>Error:</strong> Failed to load invoices. Please try again or contact support.
                    </div>
                )}
                {filterData.showFilter && (
                    <InvoiceAdvancedFilter
                        filterData={filterData}
                        setfilterData={setfilterData}
                        onFilter={handleFilter}
                        onReset={handleReset}
                        companyList={companyList}
                        sellerList={sellerList}
                        handleMultiSelect={handleMultiSelect}
                        handleDateFieldChange={handleDateFieldChange}
                        userRole={userRole}
                        roleList={roleList}
                    />
                )}
                {invoiceListStatus === 'PENDING' && <LoaderSpinner />}
                <div className="main_content cus-page-content-sec srch-pt0">
                    <InvoicesList
                        filterData={filterData}
                        setfilterData={setfilterData}
                        apiData={transformedInvoiceData}
                        totalRecords={totalRecords}
                        isLoading={invoiceListStatus === 'PENDING'}
                    />
                </div>
            </div>
        </div>
    );
};

export default Invoices;
